/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, {
	forwardRef,
	useMemo,
	useRef,
	useState,
	useEffect,
	useImperativeHandle,
} from 'react';
import { useIntl } from 'react-intl-next';
import TagGroup from '@atlaskit/tag-group';
import AddIcon from '@atlaskit/icon/core/add';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { fg } from '@atlaskit/platform-feature-flags';
import { cssMap, jsx } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ProductSelect } from '../ProductSelect';
import { InviteeList, useInviteeListStateContext } from '../InviteeList';
import { ThirdPartyConnect, useThirdPartyState } from '../ThirdParty';
import { ViralSettings, useViralSettingsContext } from '../ViralSettings';
import { LineDivider } from '../Divider';
import { SlideAnimationWrapper } from '../SlideAnimationWrapper';
import { Recaptcha } from './Recaptcha';
import { messages } from '../i18n';
import {
	type InvitePeopleFormCustomRef,
	type FeaturedUser,
	type InvitePeopleFormProps,
	isPendingUserWithId,
	isPendingUserWithEmail,
} from '../../types';
import { getFeaturedUsers } from '../../utils';
import { PluginRenderer } from './plugins';
import { useInvitePeopleContext } from '.';
import { SimpleTag } from '@atlaskit/tag';
import Tooltip from '@atlaskit/tooltip';
import Avatar from '@atlaskit/avatar';
import { useAnalytics } from '../../analytics';
import { InvitePeopleAnalytics } from './InvitePeopleAnalytics';
import type { AtlaskitSelectRefType } from '@atlaskit/select/types';
import type { UserPickerRef } from '@atlaskit/user-picker/types';
import type { OptionType } from '../InviteeList/InviteeListContext';

const styles = cssMap({
	form: {
		marginTop: token('space.0', '0px'),
		marginBottom: token('space.300', '24px'),
	},
	formSection: {
		marginBottom: token('space.200', '16px'),
	},
	featuredUserSection: {
		marginTop: token('space.100', '8px'),
	},
	featuredUserTagVisualRefresh: {
		display: 'flex',
		alignItems: 'center',
		'&:hover': {
			cursor: 'pointer',
		},
		paddingLeft: token('space.025', '2px'),
	},
	featuredUserTagLegacy: {
		paddingLeft: token('space.075', '6px'),
		paddingTop: token('space.025', '2px'),
		'&:hover': {
			cursor: 'pointer',
		},
	},
	featuredUserPressable: {
		backgroundColor: 'transparent',
		paddingTop: token('space.0', '0px'),
		paddingBottom: token('space.0', '0px'),
		paddingLeft: token('space.0', '0px'),
		paddingRight: token('space.0', '0px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& *': {
			cursor: 'pointer',
		},
	},
});

const InvitePeopleForm = forwardRef<InvitePeopleFormCustomRef, InvitePeopleFormProps>(
	(
		{
			shouldRenderProductSelect,
			productOptions,
			handleProductSelectMenuOpen,
			showEmptyError,
			maxNumberOfInputs,
			onInviteeListChange,
			selectedProducts,
			showSecondarySection,
			recaptcha,
			setRecaptcha,
		},
		ref,
	) => {
		const analytics = useAnalytics(InvitePeopleAnalytics);
		const {
			isOk: enableThirdPartyInvites,
			integrations: thirdPartyIntegrations,
			isConnected: thirdPartyConnected,
		} = useThirdPartyState();
		const { plugins, invitePromptOnLinkV3Cohort, experimental_featuredUsers } =
			useInvitePeopleContext();
		const { setUserPickerOptions } = useInviteeListStateContext();
		const { showOpenInvite, eligibleDomains, isViralSettingsEnabled } = useViralSettingsContext();
		const showViralSettingsSection =
			isViralSettingsEnabled && (showOpenInvite || eligibleDomains.length > 0);
		const hasSecondarySection =
			!invitePromptOnLinkV3Cohort &&
			(shouldRenderProductSelect || showViralSettingsSection) &&
			showSecondarySection;

		const { formatMessage } = useIntl();

		const currentProduct = selectedProducts[0];

		const isCurrentProductInitiallyUndefined = useRef(currentProduct === undefined);
		const inviteeListRef = useRef<UserPickerRef>(null);
		const productSelectRef = useRef<AtlaskitSelectRefType>(null);

		useImperativeHandle(ref, () => ({
			focusInviteeList: () => {
				if (inviteeListRef.current) {
					inviteeListRef.current.focus();
				}
			},
			focusProductSelect: () => {
				if (productSelectRef.current) {
					productSelectRef.current.focus();
				}
			},
		}));

		const secondarySectionTitle = useMemo(() => {
			if (isCurrentProductInitiallyUndefined.current) {
				if (shouldRenderProductSelect && showViralSettingsSection) {
					return fg('product-terminology-refresh')
						? messages.secondarySectionHeadingNoCurrentProductAppify
						: messages.secondarySectionHeadingNoCurrentProduct;
				} else if (shouldRenderProductSelect) {
					return fg('product-terminology-refresh')
						? messages.secondarySectionHeadingWithProductSelectorNoCurrentProductAppify
						: messages.secondarySectionHeadingWithProductSelectorNoCurrentProduct;
				} else {
					return messages.secondarySectionHeadingWithViralSettings;
				}
			}

			if (shouldRenderProductSelect && showViralSettingsSection) {
				return fg('product-terminology-refresh')
					? messages.secondarySectionHeadingAppify
					: messages.secondarySectionHeading;
			} else if (shouldRenderProductSelect) {
				return fg('product-terminology-refresh')
					? messages.secondarySectionHeadingWithProductSelectorAppify
					: messages.secondarySectionHeadingWithProductSelector;
			} else {
				return messages.secondarySectionHeadingWithViralSettings;
			}
		}, [shouldRenderProductSelect, showViralSettingsSection]);

		const [featuredUsers, setFeaturedUsers] = useState<FeaturedUser[]>([]);
		const [selectedFeaturedUsers, setSelectedFeaturedUsers] = useState<FeaturedUser[]>([]);

		useEffect(() => {
			getFeaturedUsers(experimental_featuredUsers).then((u) => {
				const users = u || [];
				setFeaturedUsers(users);
			});
		}, [experimental_featuredUsers]);

		const visibleFeaturedUsers = () => {
			const sameUser = (user1: FeaturedUser, user2: FeaturedUser) => {
				return user1.id ? user1.id === user2.id : user1.email ? user1.email === user2.email : false;
			};
			const unselectedFeaturedUsers = featuredUsers?.filter(
				(user) => !selectedFeaturedUsers.some((selected) => sameUser(selected, user)),
			);

			const visibleFeaturedUsers = unselectedFeaturedUsers.slice(0, 4);
			return visibleFeaturedUsers;
		};

		const convertFeaturedUserToOptionType = (
			userToInvite: FeaturedUser,
		): OptionType | undefined => {
			if (isPendingUserWithId(userToInvite)) {
				return { ...userToInvite, type: 'user' as const };
			}

			if (isPendingUserWithEmail(userToInvite)) {
				return {
					...userToInvite,
					id: userToInvite.email,
					name: userToInvite.name ?? userToInvite.email,
					type: 'email' as const,
				};
			}

			return undefined;
		};
		const handleAddFeaturedUser = (userToInvite: FeaturedUser) => {
			if (userToInvite) {
				const option = convertFeaturedUserToOptionType(userToInvite);
				if (!option) {
					return;
				}

				setUserPickerOptions((prevOptions: OptionType[]) => [...prevOptions, option]);
				analytics.featuredUserTagClicked({
					featuredUser: userToInvite,
				});
			}
			setSelectedFeaturedUsers((prevUsers: FeaturedUser[]) => {
				return [...prevUsers, userToInvite];
			});
		};

		const shouldShowFeaturedUsers = experimental_featuredUsers && featuredUsers.length > 0;

		const FeaturedUserTagVisualRefresh = (user: FeaturedUser) => {
			return (
				<Pressable
					key={user.id}
					xcss={styles.featuredUserPressable}
					onClick={() => handleAddFeaturedUser(user)}
					aria-label={formatMessage(messages.addUserToProjectAriaLabel, {
						userName: user.name,
					})}
				>
					<SimpleTag
						key={user.id}
						appearance="rounded"
						text={user.name ?? 'Unknown User'}
						elemBefore={
							<Box xcss={styles.featuredUserTagVisualRefresh}>
								<AddIcon label="Add icon" size="small" />
								<Avatar
									borderColor="transparent"
									size="xsmall"
									src={user.avatarUrl}
									label={`Avatar for ${user.name}`}
								/>
							</Box>
						}
						data-testid={`featured-user-${user.id}`}
					/>
				</Pressable>
			);
		};

		const FeaturedUserTagLegacy = (user: FeaturedUser) => {
			return (
				<Pressable
					key={user.id}
					xcss={styles.featuredUserPressable}
					onClick={() => handleAddFeaturedUser(user)}
					aria-label={formatMessage(messages.addUserToProjectAriaLabel, {
						userName: user.name,
					})}
				>
					<SimpleTag
						key={user.id}
						appearance="default"
						text={user.name ?? 'Unknown User'}
						elemBefore={
							<Box xcss={styles.featuredUserTagLegacy}>
								<AddIcon label="Add icon" size="small" />
							</Box>
						}
					/>
				</Pressable>
			);
		};

		const FeaturedUserTag = componentWithFG(
			'platform-component-visual-refresh',
			FeaturedUserTagVisualRefresh,
			FeaturedUserTagLegacy,
		);

		return (
			<form
				onSubmit={(e: any) => {
					e.preventDefault();
				}}
				css={styles.form}
			>
				<Box xcss={styles.formSection}>
					<InviteeList
						maxEmails={maxNumberOfInputs}
						onInviteeListChange={onInviteeListChange}
						showEmptyError={showEmptyError}
						ref={inviteeListRef}
					/>
					{shouldShowFeaturedUsers && (
						<Box xcss={styles.featuredUserSection}>
							<TagGroup>
								{visibleFeaturedUsers().map((user: FeaturedUser) => (
									<Tooltip key={user.id} content={user.tooltip ?? ''}>
										<FeaturedUserTag key={user.id} {...user} />
									</Tooltip>
								))}
							</TagGroup>
						</Box>
					)}
				</Box>
				{enableThirdPartyInvites &&
					!invitePromptOnLinkV3Cohort &&
					thirdPartyIntegrations &&
					!thirdPartyConnected && (
						<Box xcss={styles.formSection}>
							<ThirdPartyConnect />
						</Box>
					)}
				<PluginRenderer plugins={plugins} location="belowInviteeList" />
				<SlideAnimationWrapper shouldTriggerAnimation={hasSecondarySection}>
					<>
						<LineDivider />
						<Box xcss={styles.formSection}>
							<Heading size="xsmall" as="h2">
								{formatMessage(secondarySectionTitle)}
							</Heading>
						</Box>
						{shouldRenderProductSelect && (
							<Box xcss={styles.formSection}>
								<ProductSelect
									productOptions={productOptions}
									onMenuOpen={handleProductSelectMenuOpen}
									formInvalid={showEmptyError}
									ref={productSelectRef}
								/>
							</Box>
						)}
						{showViralSettingsSection && <ViralSettings product={selectedProducts[0]} />}
					</>
				</SlideAnimationWrapper>
				<PluginRenderer plugins={plugins} location="footer" />
				<Box xcss={styles.formSection}>
					<Recaptcha
						isLoading={!recaptcha?.isRecaptchaLoaded && !recaptcha?.recaptchaLoadErrorReason}
						onChange={setRecaptcha}
					/>
				</Box>
			</form>
		);
	},
);

export default InvitePeopleForm;
